import { useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { clsx } from 'clsx';
import { useToastStore } from '../../stores/toast';
import type { ToastType } from '../../stores/toast';
import { useTranslation } from 'react-i18next';

const icons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const iconColors: Record<ToastType, string> = {
  success: 'text-aurora-3',
  error:   'text-nord-11',
  info:    'text-frost-1',
  warning: 'text-aurora-2',
};

const barColors: Record<ToastType, string> = {
  success: 'bg-aurora-3',
  error:   'bg-nord-11',
  info:    'bg-frost-1',
  warning: 'bg-aurora-2',
};

function ToastItem({ id, type, title, message, duration }: { id: string; type: ToastType; title: string; message?: string; duration?: number }) {
  const { t } = useTranslation();
  const { removeToast } = useToastStore();
  const Icon = icons[type];
  const [exiting, setExiting] = useState(false);
  const [paused, setPaused] = useState(false);
  const dur = duration ?? 4000;

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(() => removeToast(id), 200);
  };

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={clsx(
        'relative flex items-start gap-3 rounded-xl border border-nord-2/60 px-4 py-3',
        'bg-nord-1/95 backdrop-blur-xl shadow-lifted',
        'min-w-[320px] max-w-[420px] overflow-hidden',
        'transition-all duration-200 ease-spring',
        exiting ? 'animate-toast-out' : 'animate-toast-in',
      )}
      role="alert"
      aria-live="polite"
    >
      <Icon className={clsx('h-5 w-5 shrink-0 mt-0.5', iconColors[type])} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-nord-6">{title}</p>
        {message && <p className="text-xs text-nord-4 mt-0.5 leading-relaxed">{message}</p>}
      </div>
      <button
        onClick={handleDismiss}
        aria-label={t('uiKit.dismissNotification', 'Dismiss notification')}
        className="shrink-0 rounded-lg p-1 text-nord-muted hover:bg-nord-2 hover:text-nord-4 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-nord-2/30">
        <div
          className={clsx('h-full rounded-full', barColors[type])}
          style={{
            animation: `progress ${dur}ms linear forwards`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        />
      </div>
    </div>
  );
}

export function ToastContainer() {
  const { toasts } = useToastStore();

  // Limit to 5 visible toasts
  const visibleToasts = toasts.slice(-5);

  if (visibleToasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-2.5">
      {visibleToasts.map((t) => (
        <ToastItem key={t.id} {...t} />
      ))}
    </div>
  );
}
